// /opt/isp_erp/frontend/src/pages/ftth/NapsMap.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  GoogleMap,
  Marker,
  MarkerClusterer,
  InfoWindow,
} from "@react-google-maps/api";
import { ftthAPI } from "../../api/ftth.api";

const containerStyle = {
  width: "100%",
  height: "80vh",
};

export default function NapsMap() {
  const [naps, setNaps] = useState([]);
  const [selectedNap, setSelectedNap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 🔍 LOG 1: Verificar si Google Maps está disponible
  console.log("🔍 [NapsMap] Google Maps disponible:", !!window.google);
  console.log("🔍 [NapsMap] Google Maps API:", !!(window.google && window.google.maps));
  console.log("🔍 [NapsMap] Componente montado");

  const center = useMemo(() => {
    console.log("🔍 [NapsMap] Calculando centro, naps:", naps.length);
    if (naps.length > 0 && naps[0].latitud && naps[0].longitud) {
      return {
        lat: parseFloat(naps[0].latitud),
        lng: parseFloat(naps[0].longitud),
      };
    }
    return { lat: -1.2884, lng: -78.6167 }; // Centro de Ecuador
  }, [naps]);

  useEffect(() => {
    console.log("🔍 [NapsMap] useEffect ejecutándose");
    cargarNaps();
  }, []);

  const cargarNaps = async () => {
    console.log("🔍 [NapsMap] Cargando NAPs...");
    setLoading(true);
    try {
      const res = await ftthAPI.getNaps();
      console.log("🔍 [NapsMap] NAPs cargados:", res.length);
      console.log("🔍 [NapsMap] Primer NAP:", res[0]);
      setNaps(res);
    } catch (err) {
      console.error("🔍 [NapsMap] Error al cargar NAPs:", err);
      setError(err.message || "Error al cargar NAPs");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-4 text-center">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
        <p className="mt-4">Cargando mapa de NAPs...</p>
        <p className="text-sm text-gray-500 mt-2">📡 Cargando puntos de red FTTH</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-red-50 border border-red-200 text-red-800 p-8 rounded-lg text-center">
          <p className="text-lg mb-2">❌ Error al cargar los NAPs</p>
          <p className="text-sm">{error}</p>
          <button
            onClick={cargarNaps}
            className="mt-4 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (naps.length === 0) {
    return (
      <div className="container mx-auto p-4">
        <h1 className="text-xl font-bold mb-4">Mapa de NAPs - Red FTTH</h1>
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-8 rounded-lg text-center">
          <p className="text-lg mb-2">📡 No hay NAPs registrados</p>
          <p className="text-sm">
            Los NAPs aparecerán aquí cuando tengas puntos de red configurados.
          </p>
          <button
            onClick={() => window.location.href = '/ftth/naps/nuevo'}
            className="mt-4 bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700"
          >
            ➕ Agregar primer NAP
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-xl font-bold mb-4">
        📡 Mapa de NAPs - Red FTTH ({naps.length})
      </h1>
      <p className="text-sm text-gray-600 mb-4">
        Puntos de red disponibles para conexiones de fibra óptica
      </p>

      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={13}
      >
        <MarkerClusterer>
          {(clusterer) =>
            naps.map((nap) => (
              <Marker
                key={nap.id}
                position={{
                  lat: parseFloat(nap.latitud),
                  lng: parseFloat(nap.longitud),
                }}
                clusterer={clusterer}
                onClick={() => setSelectedNap(nap)}
              />
            ))
          }
        </MarkerClusterer>

        {selectedNap && (
          <InfoWindow
            position={{
              lat: parseFloat(selectedNap.latitud),
              lng: parseFloat(selectedNap.longitud),
            }}
            onCloseClick={() => setSelectedNap(null)}
          >
            <div className="p-2 max-w-xs">
              <h2 className="font-bold text-lg">
                📡 {selectedNap.codigo} - {selectedNap.nombre}
              </h2>
              <p className="text-sm mt-1">
                <span className="font-semibold">Dirección:</span> {selectedNap.direccion || 'No especificada'}
              </p>
              <p className="text-sm">
                <span className="font-semibold">Puertos:</span> {selectedNap.puertos_ocupados}/{selectedNap.capacidad_puertos}
              </p>
              <p className="text-sm">
                <span className="font-semibold">Ocupación:</span> {selectedNap.porcentaje_ocupacion}%
              </p>
              <p className="text-sm">
                <span className="font-semibold">Estado:</span>
                <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                  selectedNap.estado === 'ACTIVO' ? 'bg-green-100 text-green-800' :
                  selectedNap.estado === 'LLENO' ? 'bg-red-100 text-red-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {selectedNap.estado}
                </span>
              </p>
              <button
                className="mt-2 w-full bg-blue-500 text-white text-sm py-1 px-2 rounded hover:bg-blue-600"
                onClick={() => window.open(
                  `https://www.google.com/maps/search/?api=1&query=${selectedNap.latitud},${selectedNap.longitud}`,
                  '_blank'
                )}
              >
                Ver en Google Maps
              </button>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>

      {/* 🔍 LOGS visibles en la UI para depuración */}
      <div className="mt-4 p-2 bg-gray-100 rounded text-xs font-mono">
        <p className="font-bold">🔍 Depuración:</p>
        <p>📡 NAPs cargados: {naps.length}</p>
        <p>🗺️ Google Maps disponible: {!!window.google ? "✅ Sí" : "❌ No"}</p>
        <p>📍 Centro del mapa: {center.lat}, {center.lng}</p>
        <p>🔄 Estado: {loading ? "Cargando..." : "Listo"}</p>
      </div>
    </div>
  );
}
